import { useState, useEffect, useRef } from "react"
import { router } from "@inertiajs/react"
import { ADVANCED_FILTER_KEYS } from "@/lib/project-configs"

type UseProjectFiltersOptions = {
  filters: Record<string, string>
  baseUrl: string
  urlSuffix?: string
}

export function useProjectFilters({ filters, baseUrl, urlSuffix }: UseProjectFiltersOptions) {
  const activePipeStatus = filters.pipe_status_id_eq || ""
  const [search, setSearch] = useState(filters.name_or_city_name_cont || "")
  const inputRef = useRef<HTMLInputElement>(null)
  const isFirstRender = useRef(true)
  const hasSearched = useRef(!!filters.name_or_city_name_cont)

  // Sort state from filters
  const currentSort = filters.s || ""
  const [sortColumn, sortDirection] = currentSort
    ? ([currentSort.replace(/ (asc|desc)$/, ""), currentSort.endsWith(" desc") ? "desc" : "asc"] as const)
    : (["", "asc"] as const)

  // Advanced filter popover state
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [advancedFilters, setAdvancedFilters] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {}
    for (const key of ADVANCED_FILTER_KEYS) {
      if (filters[key]) initial[key] = filters[key]
    }
    return initial
  })

  const activeAdvancedCount = Object.values(advancedFilters).filter(Boolean).length

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  // Build query preserving all current filters (no status_eq — handled by route)
  const buildQuery = (overrides: Record<string, string> = {}) => {
    const q: Record<string, string> = {}
    const searchVal = overrides.name_or_city_name_cont ?? search
    if (searchVal) q.name_or_city_name_cont = searchVal
    const pipeStatusVal = overrides.pipe_status_id_eq ?? activePipeStatus
    if (pipeStatusVal) q.pipe_status_id_eq = pipeStatusVal
    const sortVal = overrides.s ?? currentSort
    if (sortVal) q.s = sortVal
    // Preserve advanced filters
    const currentAdvanced = overrides._advanced !== undefined ? {} : advancedFilters
    for (const key of ADVANCED_FILTER_KEYS) {
      const val = overrides[key] ?? currentAdvanced[key]
      if (val) q[key] = val
    }
    return q
  }

  const navigate = (overrides: Record<string, string> = {}) => {
    router.get(baseUrl + (urlSuffix || ""), { q: buildQuery(overrides) }, { preserveState: true })
  }

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    const timeout = setTimeout(() => {
      if (search.length >= 2 || (search === "" && hasSearched.current)) {
        hasSearched.current = search.length >= 2
        navigate({ name_or_city_name_cont: search })
      }
    }, 300)
    return () => clearTimeout(timeout)
  }, [search])

  const setPipeStatusFilter = (value: string) => {
    navigate({ pipe_status_id_eq: value === "all" ? "" : value })
  }

  const setSort = (column: string) => {
    if (!column) {
      navigate({ s: "" })
      return
    }
    const dir = column === sortColumn ? (sortDirection === "asc" ? "desc" : "asc") : "asc"
    navigate({ s: `${column} ${dir}` })
  }

  const toggleSortDirection = () => {
    if (!sortColumn) return
    const newDir = sortDirection === "asc" ? "desc" : "asc"
    navigate({ s: `${sortColumn} ${newDir}` })
  }

  const resetAdvancedFilters = () => {
    setAdvancedFilters({})
    const overrides: Record<string, string> = { _advanced: "" }
    for (const key of ADVANCED_FILTER_KEYS) {
      overrides[key] = ""
    }
    navigate(overrides)
  }

  const setAdvanced = (key: string, value: string) => {
    const next = { ...advancedFilters }
    if (!value) {
      delete next[key]
    } else {
      next[key] = value
    }
    setAdvancedFilters(next)
    const overrides: Record<string, string> = {}
    for (const k of ADVANCED_FILTER_KEYS) {
      overrides[k] = next[k] || ""
    }
    navigate(overrides)
  }

  // Debounced version for text/number/date inputs
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(null)
  const setAdvancedDebounced = (key: string, value: string) => {
    const next = { ...advancedFilters }
    if (!value) {
      delete next[key]
    } else {
      next[key] = value
    }
    setAdvancedFilters(next)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      const overrides: Record<string, string> = {}
      for (const k of ADVANCED_FILTER_KEYS) {
        overrides[k] = next[k] || ""
      }
      navigate(overrides)
    }, 400)
  }

  return {
    search,
    setSearch,
    inputRef,
    activePipeStatus,
    setPipeStatusFilter,
    sortColumn,
    sortDirection,
    setSort,
    toggleSortDirection,
    advancedOpen,
    setAdvancedOpen,
    advancedFilters,
    activeAdvancedCount,
    setAdvanced,
    setAdvancedDebounced,
    resetAdvancedFilters,
  }
}
